import { notEmpty, type Optional } from '@festina-life/core'

import type { CollectionType } from '../components/fplusExpression/types'
import { isEnumMetaType, type MetaModel } from '../types/metaModel'
import type { EnumDefinition } from '../uiModels/enumLiterals/types'

const fPlusCollectionTypes = { List: 'list', Set: 'set' } as const

export const extractCollectionType = (returnType: string) => {
  const matchableCollectionTypes = Object.keys(fPlusCollectionTypes).join('|')
  const match = returnType.match(
    new RegExp(`^(${matchableCollectionTypes})<(\\w+)>$`),
  )

  return match
    ? {
        returnType: match[2],
        collectionType:
          fPlusCollectionTypes[match[1] as keyof typeof fPlusCollectionTypes],
      }
    : undefined
}

export const wrapperTypeToCollectionType = (
  wrapperType: Optional<string>,
): Optional<CollectionType> =>
  wrapperType && wrapperType in fPlusCollectionTypes
    ? fPlusCollectionTypes[wrapperType as keyof typeof fPlusCollectionTypes]
    : undefined

export const typeOptions = ({
  typeName,
  metaModel,
  configuredEnums,
}: {
  typeName: Optional<string>
  metaModel: MetaModel
  configuredEnums: Map<string, EnumDefinition>
}): Optional<string[]> => {
  if (!typeName) {
    return null
  }

  const matchedCollectionType = extractCollectionType(typeName)

  const internalType = matchedCollectionType?.returnType ?? typeName
  const metaModelType = metaModel.types.get(internalType)

  if (
    configuredEnums.get(internalType) ||
    (metaModelType && isEnumMetaType(metaModelType))
  ) {
    const modelTypeValues =
      metaModelType && isEnumMetaType(metaModelType)
        ? metaModelType?.values
        : undefined

    const allValues = new Set<string>([
      ...(modelTypeValues ?? []),
      ...(configuredEnums.get(internalType)?.literals ?? [])
        .map((literal) =>
          literal.type === 'configured'
            ? literal.internalOperation.entity?.name
            : null,
        )
        .filter(notEmpty),
    ])

    return Array.from(allValues)
  }

  return null
}
